const K9_UNDO_DELAY = 8000;
const K9_UNDO_MIN = 2000;

// K9: line properties as an object. Liquid's `item.properties | json` gives an array of [key, value] pairs,
// /cart.js an object (or null); /cart/add.js only accepts an object.
function k9Properties(properties) {
  return Array.isArray(properties) ? Object.fromEntries(properties) : properties || {};
}

// K9: /cart/add.js body that puts a removed line back (spec rule 8). Vitals groups the lines of an offer under one
// _oid and offers nothing more on a line already tagged: an _oid no line of cartItems (the cart read at Undo) still
// carries is dropped, as a fresh add from the product page would; kept when the cart could not be read.
function k9UndoRequest(line, sections, sectionsUrl, cartItems) {
  const item = { id: line.id, quantity: line.quantity };
  const properties = { ...k9Properties(line.properties) };
  const oid = properties._oid;
  if (oid && cartItems && !cartItems.some((cartItem) => k9Properties(cartItem.properties)._oid === oid)) {
    delete properties._oid;
  }
  if (Object.keys(properties).length) item.properties = properties;
  if (line.selling_plan) item.selling_plan = line.selling_plan;
  return { items: [item], sections, sections_url: sectionsUrl };
}

// K9: true when the cart no longer holds the line (same variant and properties), or holds fewer units.
function k9LineWasRemoved(line, items) {
  const key = (properties) => JSON.stringify(Object.entries(k9Properties(properties)).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
  const wanted = key(line.properties);
  const same = (items || []).filter((item) => item.id === line.id && key(item.properties) === wanted);
  return same.reduce((total, item) => total + item.quantity, 0) < line.quantity;
}

function k9RemovedText(template, title) {
  return template.replace('[title]', title);
}

// K9: time left on the Undo timer after a pause: what remained, never under the minimum so the user can come back.
function k9UndoRemaining(remaining, elapsed) {
  return Math.max(K9_UNDO_MIN, remaining - elapsed);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // The overlay is replaced by renderContents; delegate from the persistent host.
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    this.addEventListener('focusin', this.onDrawerFocusIn.bind(this));
    this.setHeaderCartIconAccessibility();
    // Capture phase: runs before cart.js removes the line and redraws the drawer.
    this.addEventListener('click', this.removeAtMinimum.bind(this), true);
    this.addEventListener('click', this.rememberRemoval.bind(this), true);
    this.addEventListener('click', this.onUndoClick.bind(this));
    // The message is recreated by renderContents: listen from the host. The timer pauses while it has the focus or the
    // mouse over it (touch validates at once, so it is ignored).
    this.addEventListener('focusin', this.onUndoFocusIn.bind(this));
    this.addEventListener('focusout', this.onUndoFocusOut.bind(this));
    this.addEventListener('pointerover', (event) => this.onUndoPointer(event, true));
    this.addEventListener('pointerout', (event) => this.onUndoPointer(event, false));
    if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) {
      this.keepFocusInDrawer();
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.dispatchEvent(new Event('k9:drawer-open'));
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = containerToTrapFocusOn.querySelector('.drawer__close') || containerToTrapFocusOn;
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    // Lot 4: product page offers resume their view timers; the drawer's views are sent.
    this.dispatchEvent(new Event('k9:drawer-close'));
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Redraws of an open drawer refresh its focus trap without waiting for another opening transition.
  renderContents(parsedState, { reopen = true, sectionDocuments } = {}) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // Reuse validated documents when supplied; parsing is synchronous, DOM writes are batched in rAF.
    const updates = this.getSectionsToRender().map((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const newHTML = sectionElement ? this.getSectionInnerHTML(parsedState.sections[section.id], section.selector, sectionDocuments?.[section.id]) : null;
      return { sectionElement, newHTML };
    });

    requestAnimationFrame(() => {
      updates.forEach(({ sectionElement, newHTML }) => {
        if (!sectionElement || newHTML === null) return;
        sectionElement.innerHTML = newHTML;
      });
      window.K9OfferLoader?.scan(this);

      this.bumpCount();
      if (this.classList.contains('active')) this.keepFocusInDrawer();
      else if (reopen || !this.classList.contains('active')) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section', sectionDocument) {
    return (sectionDocument || new DOMParser().parseFromString(html, 'text/html')).querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  bumpCount() {
    this.querySelector('.k9-cart__count')?.classList.add('k9-cart__count--bump');
  }

  // K9: at the minimum, cart.js refuses the quantity "−" would set (0 left in the field, article kept). The click is
  // stopped before the quantity selector and goes to the line's bin instead, so the removal offers Undo.
  removeAtMinimum(event) {
    const minus = event.target.closest('button[name="minus"]');
    const line = minus?.closest('.k9-cart-line');
    const input = line?.querySelector('.quantity__input');
    const bin = line?.querySelector('cart-remove-button');
    if (!input || !bin || Number(input.value) > (Number(input.dataset.min) || 1)) return;
    event.preventDefault();
    event.stopPropagation();
    bin.click();
  }

  // A line without data-k9-line (gift line: putting back a Vitals gift bills it at full price) gets no Undo message.
  rememberRemoval(event) {
    const button = event.target.closest('cart-remove-button');
    if (!button) return;
    const row = button.closest('.k9-cart-line');
    const data = row?.dataset.k9Line;
    try {
      // index: one-based position of the line, where the message takes its place in the list.
      this.pendingRemoval = data ? { ...JSON.parse(data), at: Date.now(), index: Number(button.dataset?.index) || 0 } : null;
    } catch {
      this.pendingRemoval = null;
    }
  }

  onCartUpdate(event) {
    if (event.source !== 'cart-items') return;
    const removed = this.pendingRemoval;
    this.pendingRemoval = null;
    // A failed removal publishes nothing, so a later update must not show a message for a line still in the cart.
    if (removed && !event.cartData?.errors && Date.now() - removed.at <= 10000 && k9LineWasRemoved(removed, event.cartData?.items)) {
      this.showUndo(removed);
      return;
    }
    // Any other redraw of the list while a message is shown (Undo or its error): put it back in its place. Not after a
    // full redraw (renderContents), which brings its own hidden message: the shown one is then dismissed, as before.
    if (this.undoNode && !this.undoNode.isConnected && !this.undoNode.hidden && !this.querySelector('#CartDrawer-Undo')) {
      this.placeUndo(this.undoNode);
    }
  }

  // The message takes the place of the removed line in the list (maquette v4, owner, 25 Oct.). cart.js redraws the
  // list without it: the node, its timer and its listeners are kept and put back (onCartUpdate). With no list left
  // (empty cart), it keeps its own row under the panel, its place in the markup.
  placeUndo(toast) {
    this.undoNode = toast;
    this.undoSlot?.remove();
    const list = this.querySelector('.k9-cart__lines');
    if (!list) {
      const home = document.getElementById('CartDrawer');
      if (home && toast.parentElement !== home && !home.querySelector(':scope > #CartDrawer-Undo')) home.append(toast);
      return;
    }
    if (!this.undoSlot) {
      this.undoSlot = document.createElement('li');
      this.undoSlot.className = 'k9-cart-undo';
    }
    // Only this message: after a full redraw, the slot may still hold the previous one, detached with the old list.
    this.undoSlot.replaceChildren(toast);
    list.insertBefore(this.undoSlot, list.children[this.undoIndex - 1] || null);
  }

  showUndo(line) {
    // After a redraw of the list, the message shown for a previous removal is no longer in the drawer.
    const toast = this.querySelector('#CartDrawer-Undo') || this.undoNode;
    if (!toast) return;
    clearTimeout(this.undoTimer);
    this.undoLine = line;
    this.undoIndex = line.index;
    this.placeUndo(toast);
    this.undoFocused = toast.contains(document.activeElement);
    this.undoHovered = false;
    this.undoPending = false;
    toast.querySelector('.k9-cart-toast__undo').disabled = false;
    toast.hidden = false;
    // The message sits outside the zones cart.js traps focus in: include it, keeping the focused element.
    const drawer = document.getElementById('CartDrawer');
    if (drawer && document.activeElement) trapFocus(drawer, document.activeElement);
    // Set the text once visible so screen readers announce it.
    requestAnimationFrame(() => {
      toast.querySelector('.k9-cart-toast__text').textContent = k9RemovedText(toast.dataset.removed, line.title);
    });
    this.startUndoTimer(K9_UNDO_DELAY);
  }

  startUndoTimer(delay) {
    clearTimeout(this.undoTimer);
    this.undoLeft = delay;
    this.undoStarted = Date.now();
    this.undoTimer = setTimeout(() => this.hideUndo(), delay);
    if (this.undoFocused || this.undoHovered) this.pauseUndo();
  }

  pauseUndo() {
    if (!this.undoTimer) return;
    clearTimeout(this.undoTimer);
    this.undoTimer = null;
    this.undoLeft = k9UndoRemaining(this.undoLeft, Date.now() - this.undoStarted);
  }

  resumeUndo() {
    const toast = this.querySelector('#CartDrawer-Undo');
    // Not while the Undo request is in flight; after a failure the error message resumes too.
    if (this.undoTimer || this.undoFocused || this.undoHovered || this.undoPending || !toast || toast.hidden) return;
    this.startUndoTimer(this.undoLeft);
  }

  onUndoFocusIn(event) {
    const toast = this.querySelector('#CartDrawer-Undo');
    if (!toast || !toast.contains(event.target)) return;
    this.undoFocused = true;
    this.pauseUndo();
  }

  onUndoFocusOut(event) {
    const toast = this.querySelector('#CartDrawer-Undo');
    if (!toast || !toast.contains(event.target) || toast.contains(event.relatedTarget)) return;
    this.undoFocused = false;
    this.resumeUndo();
  }

  onUndoPointer(event, inside) {
    const toast = this.querySelector('#CartDrawer-Undo');
    if (!toast || event.pointerType === 'touch' || !toast.contains(event.target) || toast.contains(event.relatedTarget)) return;
    this.undoHovered = inside;
    if (inside) this.pauseUndo();
    else this.resumeUndo();
  }

  // Same zones as open(): the empty block when the cart is empty, else the whole drawer.
  focusTrapContainer() {
    const toast = this.querySelector('#CartDrawer-Undo');
    if (toast && !toast.hidden) return document.getElementById('CartDrawer');
    const empty = this.querySelector('.drawer__inner-empty');
    return this.classList.contains('is-empty') && empty ? empty : document.getElementById('CartDrawer');
  }

  restoreFocus() {
    const empty = this.querySelector('.drawer__inner-empty');
    if (this.classList.contains('is-empty') && empty) {
      trapFocus(empty, empty.querySelector('a') || empty);
      return;
    }
    const target = this.querySelector('.cart-item__name') || this.querySelector('.drawer__close');
    if (target) trapFocus(document.getElementById('CartDrawer'), target);
  }

  // After Undo or a redraw, focus stays on an element still in the drawer, else goes to the header close button.
  // Set the trap again. Not .drawer__inner: trapFocus handles Shift+Tab only from the container or its first element, and the
  // close button is that first element.
  keepFocusInDrawer() {
    const drawer = this.focusTrapContainer();
    const close = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty .drawer__close')
      : this.querySelector('.drawer__header .drawer__close');
    if (!this.classList.contains('active') || !drawer || !close) return;
    const target = drawer.contains(document.activeElement) ? document.activeElement : close;
    // trapFocus arms its Tab handler on the focusin of the first or last element; focus() on the element that already
    // has the focus fires none, so the focus leaves it first.
    if (target === document.activeElement) target.blur();
    trapFocus(drawer, target);
  }

  // Vitals can replace controls after our redraw. Rearm on focus before the event reaches
  // document, so trapFocus sees the current first/last controls and handles Shift+Tab.
  onDrawerFocusIn(event) {
    if (!this.classList.contains('active')) return;
    const container = this.focusTrapContainer();
    if (container?.contains(event.target)) trapFocus(container, event.target);
  }

  hideUndo() {
    clearTimeout(this.undoTimer);
    this.undoLine = null;
    this.undoTimer = null;
    this.undoPending = false;
    this.undoFocused = false;
    this.undoHovered = false;
    const toast = this.querySelector('#CartDrawer-Undo');
    if (!toast) return;
    const hadFocus = toast.contains(document.activeElement);
    toast.querySelector('.k9-cart-toast__undo').disabled = true;
    toast.hidden = true;
    // Back to its place in the markup, out of the list.
    const home = document.getElementById('CartDrawer');
    if (home && toast.parentElement !== home && !home.querySelector(':scope > #CartDrawer-Undo')) home.append(toast);
    this.undoSlot?.remove();
    if (hadFocus) {
      this.restoreFocus();
    } else if (this.classList.contains('active') && this.contains(document.activeElement)) {
      // The trap still ends on the Undo button: compute it again without it, keeping the focused element.
      const container = this.focusTrapContainer();
      if (container) trapFocus(container, document.activeElement);
    }
    toast.querySelector('.k9-cart-toast__text').textContent = '';
  }

  onUndoClick(event) {
    const button = event.target.closest('.k9-cart-toast__undo');
    const line = this.undoLine;
    if (!button || !line) return;
    clearTimeout(this.undoTimer);
    this.undoTimer = null;
    this.undoPending = true;
    button.disabled = true;
    const sections = this.getSectionsToRender().map((section) => section.id);
    // The cart as it is now (Vitals may have removed the other lines of an offer): see k9UndoRequest.
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => cart.items)
      .catch(() => undefined)
      .then((cartItems) => {
        const body = JSON.stringify(k9UndoRequest(line, sections, window.location.pathname, cartItems));
        return fetch(`${routes.cart_add_url}.js`, { ...fetchConfig(), body });
      })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message);
        this.hideUndo();
        this.classList.remove('is-empty');
        this.renderContents(state, { reopen: false });
        // Queued after the redraw of renderContents, so the focus lands in the new content.
        requestAnimationFrame(() => this.keepFocusInDrawer());
        // Other subscribers may update content: keep the focus trap valid after their work settles.
        Promise.resolve(
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-undo', cartData: state, productVariantId: line.id })
        )
          .catch(() => {})
          .then(() => this.keepFocusInDrawer());
      })
      .catch(() => {
        const toast = this.querySelector('#CartDrawer-Undo');
        if (toast) toast.querySelector('.k9-cart-toast__text').textContent = toast.dataset.error;
        this.undoLine = null;
        this.undoPending = false;
        this.startUndoTimer(K9_UNDO_DELAY);
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
